import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { visaApi } from "@/lib/visa";
import { fmtDate, fmtDateTime, countryFlag } from "@/lib/format";
import { toast } from "sonner";
import { getApiErrorMessage, api } from "@/lib/api";
import { Car, ExternalLink, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const roleBase = (role) => ({ admin: "/admin", coordinator: "/coordinator", partner: "/partner", patient: "/portal" }[role] || "/admin");

export default function AirportPickup() {
  const { user } = useAuth();
  const base = roleBase(user?.role);
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => visaApi.pickups().then(setItems).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const savePickup = async (id, patch) => {
    try {
      await api.patch(`/visa/requests/${id}`, {
        travel_details: patch,
      });
      toast.success("Pickup updated");
      load();
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  return (
    <div data-testid="airport-pickup-page">
      <PageHeader eyebrow="International Services" title="Airport Pickup"
        subtitle="Schedule vehicles and drivers for arriving patients." />
      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : items.length === 0 ? (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-10 text-center text-sm text-[#8E9992]">
          <Car className="mx-auto mb-2 text-[#8E9992]" size={28} />
          No pickup requests pending.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => <PickupCard key={r.id} r={r} onSave={savePickup} onOpen={() => nav(`${base}/visa/${r.id}`)} />)}
        </div>
      )}
    </div>
  );
}

function PickupCard({ r, onSave, onOpen }) {
  const t = r.travel_details || {};
  const [flight, setFlight] = useState(t.flight_number || "");
  const [notes, setNotes] = useState(t.pickup_notes || "");
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl p-5" data-testid={`pickup-card-${r.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-[#2A332C]">{r.patient_info?.full_name} <span className="text-xs text-[#8E9992] font-normal">· {countryFlag(r.patient_info?.nationality)} {r.patient_info?.nationality}</span></div>
          <div className="text-xs text-[#8E9992] mt-0.5">Arrival {fmtDate(t.proposed_arrival_date)} at {t.arrival_airport || "—"}</div>
        </div>
        <button onClick={onOpen} className="text-[#2A4D3E]"><ExternalLink size={14} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold mb-1">Flight number</div>
          <input value={flight} onChange={(e) => setFlight(e.target.value)} className="w-full border border-[#E8E6E1] rounded-lg px-3 py-2 text-sm" data-testid={`flight-${r.id}`} />
        </label>
        <label className="block md:col-span-2">
          <div className="text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold mb-1">Pickup notes</div>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Driver, vehicle, meet point…" className="w-full border border-[#E8E6E1] rounded-lg px-3 py-2 text-sm" data-testid={`notes-${r.id}`} />
        </label>
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={() => onSave(r.id, { ...t, flight_number: flight, pickup_notes: notes })}
          className="bg-[#2A4D3E] text-white rounded-full px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5" data-testid={`save-pickup-${r.id}`}>
          <Save size={12} /> Save pickup details
        </button>
      </div>
    </div>
  );
}
